/**
 * Chatbot Service
 * Handles communication with the AI chatbot backend
 */

import { API_ENDPOINTS } from "../config/api";

export const buildChatHistory = (messages = [], limit = 10) =>
  messages
    .filter((m) => (m.type === "user" || m.type === "bot") && m.text && !m.isError)
    .slice(-limit)
    .map((m) => ({
      role: m.type === "user" ? "user" : "assistant",
      content: m.text,
    }));

/**
 * Send a message to the chatbot
 * @param {string} message - User's message
 * @param {Array} chatHistory - Optional chat history for context
 * @returns {Promise<Object>} - Chatbot response
 */
export const sendChatMessage = async (message, chatHistory = []) => {
  try {
    const response = await fetch(API_ENDPOINTS.CHATBOT.CHAT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        message,
        chat_history: chatHistory,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || data.response || "Failed to get chatbot response");
    }

    return data;
  } catch (error) {
    console.error("Chatbot service error:", error);
    throw error;
  }
};

/**
 * Send a message to the seller chatbot
 */
export const sendSellerChatMessage = async (message, chatHistory = []) => {
  try {
    const response = await fetch(API_ENDPOINTS.CHATBOT.SELLER_CHAT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        message,
        chat_history: chatHistory,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || data.response || "Failed to get seller chatbot response");
    }

    return data;
  } catch (error) {
    console.error("Seller Chatbot service error:", error);
    throw error;
  }
};

export const getChatSuggestions = async () => {
  try {
    const response = await fetch(API_ENDPOINTS.CHATBOT.SUGGESTIONS, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "Failed to get suggestions");
    }

    return data.suggestions || [];
  } catch (error) {
    console.error("Failed to fetch suggestions:", error);
    return [
      "Find cameras near me",
      "How does renting work?",
      "What's the cancellation policy?",
      "Show affordable bikes to rent",
    ];
  }
};

export const getSellerChatSuggestions = async () => {
  try {
    const response = await fetch(API_ENDPOINTS.CHATBOT.SELLER_SUGGESTIONS, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "Failed to get seller suggestions");
    }

    return data.suggestions || [];
  } catch (error) {
    console.error("Failed to fetch seller suggestions:", error);
    return [
      "How do I list an item?",
      "What are the fees?",
      "Tips to get more bookings?",
    ];
  }
};

export const checkChatbotHealth = async () => {
  try {
    const response = await fetch(API_ENDPOINTS.CHATBOT.HEALTH, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    return await response.json();
  } catch (error) {
    console.error("Health check failed:", error);
    return {
      success: false,
      status: "unhealthy",
      error: error.message,
    };
  }
};
